import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader, Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Badge } from 'reactstrap';
import { MoreHorizontal, TrendingUp, TrendingDown, DollarSign, Users, Calendar, Star } from 'react-feather';
import ReactApexChart from 'react-apexcharts';
import './charts-wrapper.css';

const ChartComponent = ({
  title,
  chartType,
  series,
  options,
  colors,
  dropdownItems = ["daily", "weekly", "monthly"],
  onSelect,
  icon,
  value,
  change,
  isLoading = false,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const toggleMenu = () => setIsMenuOpen(prev => !prev);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Card className={`chart-card ${isVisible ? 'chart-card-visible' : ''}`}>
      <div className="chart-card-overlay"></div>

      <CardHeader className="chart-card-header">
        <div className="d-flex justify-content-between align-items-start">
          <div className="d-flex align-items-center">
            {icon && (
              <div className="chart-icon-wrapper me-3">
                {icon}
              </div>
            )}
            <div>
              <h5 className="chart-title mb-1">{title}</h5>
              {value && (
                <div className="d-flex align-items-center gap-2">
                  {/* <span className="chart-value">{value}</span> */}
                  {/* {change !== undefined && (
                    <Badge
                      color={change >= 0 ? "success" : "danger"}
                      className={`chart-badge ${change >= 0 ? 'chart-badge-positive' : 'chart-badge-negative'}`}
                    >
                      {change >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                      <span className="ms-1">{Math.abs(change)}%</span>
                    </Badge>
                  )} */}
                </div>
              )}
            </div>
          </div>

          {/* <Dropdown isOpen={isMenuOpen} toggle={toggleMenu}>
            <DropdownToggle className="chart-dropdown-toggle">
              <MoreHorizontal size={16} />
            </DropdownToggle>
            <DropdownMenu end className="chart-dropdown-menu">
              {dropdownItems.map((item) => (
                <DropdownItem
                  key={item}
                  onClick={() => {
                    onSelect?.(item);
                    toggleMenu();
                  }}
                  className="chart-dropdown-item"
                >
                  {item.charAt(0).toUpperCase() + item.slice(1)}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown> */}
        </div>
      </CardHeader>

      <CardBody className="chart-card-body">
        {isLoading ? (
          <div className="chart-loading">
            <div className="chart-spinner"></div>
          </div>
        ) : (
          <div className="chart-container">
            <ReactApexChart
              options={{ ...options, colors }}
              series={series}
              type={chartType}
              height={300}
            />
          </div>
        )}
      </CardBody>
    </Card>
  );
};

// Enhanced color schemes based on your brand color #33bdbf
const chartColors = {
  revenue: ["#33bdbf", "#2da8aa", "#279396"],
  subscriptions: ["#33bdbf", "#4dd0d2", "#66d9db"],
  classes: ["#ff6b6b", "#ff5252", "#ff3d3d"],
  orders: ["#4ecdc4", "#45b7aa", "#3ca190"],
  payments: ["#ffe66d", "#ffd93d", "#ffcc02"],
  reviews: ["#fff9c4", "#ffeb3b", "#fbc02d", "#f9a825", "#f57f17"]
};

// Enhanced chart options with your brand styling
const createBaseOptions = (type) => ({
  chart: {
    type,
    height: 350,
    toolbar: { show: false },
    background: "transparent",
    animations: {
      enabled: true,
      easing: "easeinout",
      speed: 800,
      animateGradually: {
        enabled: true,
        delay: 150,
      },
      dynamicAnimation: {
        enabled: true,
        speed: 350,
      },
    },
  },
  theme: {
    mode: "light",
  },
  grid: {
    borderColor: "rgba(51, 189, 191, 0.1)",
    strokeDashArray: 3,
    xaxis: { lines: { show: false } },
    yaxis: { lines: { show: true } },
  },
  tooltip: {
    theme: "light",
    style: {
      fontSize: "12px",
      fontFamily: "'Inter', sans-serif",
    },
    x: {
      show: true,
    },
    y: {
      formatter: (val) => `${val.toLocaleString()}`,
    },
  },
  legend: {
    show: true,
    position: "top",
    horizontalAlign: "right",
    fontSize: "12px",
    fontFamily: "'Inter', sans-serif",
    fontWeight: 500,
  },
});

const baseLineOptions = {
  ...createBaseOptions("line"),
  stroke: {
    curve: "smooth",
    width: 3,
    lineCap: "round",
  },
  fill: {
    type: "gradient",
    gradient: {
      shade: "light",
      type: "vertical",
      shadeIntensity: 0.5,
      gradientToColors: undefined,
      inverseColors: false,
      opacityFrom: 0.8,
      opacityTo: 0.1,
      stops: [0, 100],
    },
  },
  markers: {
    size: 6,
    strokeWidth: 3,
    strokeColors: "#fff",
    hover: { size: 8 },
  },
  xaxis: {
    labels: {
      style: {
        colors: "#64748B",
        fontSize: "12px",
        fontFamily: "'Inter', sans-serif",
      },
    },
  },
  yaxis: {
    labels: {
      style: {
        colors: "#64748B",
        fontSize: "12px",
        fontFamily: "'Inter', sans-serif",
      },
    },
  },
};

const baseBarOptions = {
  ...createBaseOptions("bar"),
  plotOptions: {
    bar: {
      borderRadius: 8,
      columnWidth: "60%",
      distributed: false,
    },
  },
  fill: {
    type: "gradient",
    gradient: {
      shade: "light",
      type: "vertical",
      shadeIntensity: 0.3,
      gradientToColors: undefined,
      inverseColors: false,
      opacityFrom: 0.9,
      opacityTo: 0.7,
      stops: [0, 100],
    },
  },
  xaxis: {
    labels: {
      style: {
        colors: "#64748B",
        fontSize: "12px",
        fontFamily: "'Inter', sans-serif",
      },
    },
  },
  yaxis: {
    labels: {
      style: {
        colors: "#64748B",
        fontSize: "12px",
        fontFamily: "'Inter', sans-serif",
      },
    },
  },
};

const baseDonutOptions = {
  ...createBaseOptions("donut"),
  dataLabels: {
    enabled: true,
    formatter: (val) => `${val.toFixed(1)}%`,
    style: {
      fontSize: "14px",
      fontWeight: "600",
      fontFamily: "'Inter', sans-serif",
    },
  },
  plotOptions: {
    pie: {
      donut: {
        size: "70%",
        labels: {
          show: true,
          name: {
            show: true,
            fontSize: "18px",
            fontFamily: "'Inter', sans-serif",
            fontWeight: 600,
            offsetY: -10,
          },
          value: {
            show: true,
            fontSize: "24px",
            fontFamily: "'Inter', sans-serif",
            fontWeight: 700,
            offsetY: 10,
            formatter: (val) => `${val}`,
          },
          total: {
            show: true,
            showAlways: true,
            label: "Total",
            fontSize: "16px",
            fontFamily: "'Inter', sans-serif",
            fontWeight: 600,
            formatter: function (w) {
              const total = w.globals.seriesTotals.reduce((a, b) => a + b, 0);
              return `${total}`;
            },
          },
        },
      },
    },
  },
};

const ChartsWrapper = ({ dashboardData }) => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  // Extract data with fallbacks
  const {
    revenueTrend = [

    ],
    subscriptionRevenue = [

    ],
    classBookings = [

    ],
    orderTrend = [

    ],
    pendingPayouts = [

    ],
    ratingDistribution = {
      breakdown: [

      ],
      totalReviews: 250,
    },
  } = dashboardData || {};

  // Prepare chart data
  const charts = [
    {
      title: "Revenue Trends",
      type: "line",
      icon: <DollarSign size={20} />,
      value: "KWD 24,500",
      change: 12.5,
      colors: chartColors.revenue,
      options: {
        ...baseLineOptions,
        xaxis: {
          ...baseLineOptions.xaxis,
          categories: revenueTrend.map((item) => item.date),
          labels: {
            rotate: -45,
            style: { fontSize: '10px' },
          },
        },
      },
      series: [{ name: "Revenue (KWD)", data: revenueTrend.map((item) => item.value) }],
    },
    {
      title: "Subscription Revenue",
      type: "bar",
      icon: <Users size={20} />,
      value: "KWD 18,165",
      change: 8.2,
      colors: chartColors.subscriptions,
      options: {
        ...baseBarOptions,
        xaxis: {
          ...baseBarOptions.xaxis,
          categories: subscriptionRevenue.map((item) =>
            item.name.length > 10 ? item.name.slice(0, 10) + "…" : item.name
          ),
          labels: {
            rotate: -45,
            style: { fontSize: '10px' },
          },
        },
      },
      series: [{ name: "Revenue", data: subscriptionRevenue.map((item) => item.revenue) }],
    },
    {
      title: "Class Bookings",
      type: "bar",
      icon: <Calendar size={20} />,
      value: "143 Bookings",
      change: -2.1,
      colors: chartColors.classes,
      options: {
        ...baseBarOptions,
        xaxis: {
          ...baseBarOptions.xaxis,
          categories: classBookings.map((item) =>
            item.className.length > 10 ? item.className.slice(0, 10) + "…" : item.className
          ),
          labels: {
            rotate: -45,
            style: { fontSize: '10px' },
          },
        },
      },
      series: [{ name: "Bookings", data: classBookings.map((item) => item.totalBookings) }],
    },
    {
      title: "Order Trends",
      type: "line",
      icon: <TrendingUp size={20} />,
      value: "650 Orders",
      change: 15.3,
      colors: chartColors.orders,
      options: {
        ...baseLineOptions,
        xaxis: {
          ...baseLineOptions.xaxis,
          categories: orderTrend.map((item) => item.date),
          labels: {
            rotate: -45,
            style: { fontSize: '10px' },
          },
        },
      },
      series: [{ name: "Orders", data: orderTrend.map((item) => item.value) }],
    },
    {
      title: "Pending Payouts",
      type: "bar",
      icon: <DollarSign size={20} />,
      value: "KWD 5,400",
      change: -5.7,
      colors: chartColors.payments,
      options: {
        ...baseBarOptions,
        xaxis: {
          ...baseBarOptions.xaxis,
          categories: pendingPayouts.map((item) => item.date),
          labels: {
            rotate: -45,
            style: { fontSize: '10px' },
          },
        },
      },
      series: [{ name: "Pending Payout", data: pendingPayouts.map((item) => item.value) }],
    },
    {
      title: "Review Distribution",
      type: "donut",
      icon: <Star size={20} />,
      value: `${ratingDistribution.totalReviews} Reviews`,
      change: 3.8,
      colors: chartColors.reviews,
      options: {
        ...baseDonutOptions,
        labels: (() => {
          const sorted = [...ratingDistribution.breakdown].sort((a, b) => b.count - a.count);
          const top = sorted.slice(0, 5);
          const others = sorted.slice(5);
          const otherCount = others.reduce((sum, curr) => sum + curr.count, 0);
          return [...top.map((item) => `${item.rating}★`), ...(otherCount > 0 ? ['Others'] : [])];
        })(),
        legend: {
          show: true,
          position: "bottom",
          fontSize: "10px",
        },
      },
      series: (() => {
        const sorted = [...ratingDistribution.breakdown].sort((a, b) => b.count - a.count);
        const top = sorted.slice(0, 5);
        const others = sorted.slice(5);
        const otherCount = others.reduce((sum, curr) => sum + curr.count, 0);
        return [...top.map((item) => item.count), ...(otherCount > 0 ? [otherCount] : [])];
      })(),
    },
  ];


  return (
    <div className="dashboard-wrapper">
      <Container fluid className="dashboard-container">
        {/* Header */}
        <div className="dashboard-header mb-5">
          <h1 className="dashboard-title">Analytics Dashboard</h1>
          <p className="dashboard-subtitle">Track your business performance with beautiful insights</p>
        </div>

        {/* Charts Grid */}
        <Row className="g-4">
          {charts.map((chart, index) => (
            <Col xl="6" lg="6" md="12" key={chart.title}>
              <div
                className="chart-wrapper"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <ChartComponent
                  title={chart.title}
                  chartType={chart.type}
                  colors={chart.colors}
                  options={chart.options}
                  series={chart.series}
                  icon={chart.icon}
                  value={chart.value}
                  change={chart.change}
                  isLoading={isLoading}
                  onSelect={(item) => console.log(`Selected ${item} for ${chart.title}`)}
                />
              </div>
            </Col>
          ))}
        </Row>
      </Container>
    </div>
  );
};

export default ChartsWrapper;
